import { logger } from "../utils/logger";

const log = (...args) => logger.debug("[ACCESS]", ...args);
const warn = (...args) => logger.warn("[ACCESS WARN]", ...args);

export const PERMISSIONS = {
  ALL: "*",
  DASHBOARD_VIEW: "DASHBOARD:VIEW",
  PROGRAM_VIEW: "PROGRAM:VIEW",
  PROGRAM_ADD: "PROGRAM:ADD",
  PROGRAM_UPDATE: "PROGRAM:UPDATE",
  PROGRAM_DELETE: "PROGRAM:DELETE",
  SUBPROGRAM_VIEW: "SUBPROGRAM:VIEW",
  SUBPROGRAM_ADD: "SUBPROGRAM:ADD",
  SUBPROGRAM_UPDATE: "SUBPROGRAM:UPDATE",
  SUBPROGRAM_DELETE: "SUBPROGRAM:DELETE",
  ATTENDANCE_VIEW: "ATTENDANCE:VIEW",
  ATTENDANCE_ADD: "ATTENDANCE:ADD",
  ATTENDANCE_UPDATE: "ATTENDANCE:UPDATE",
  ATTENDANCE_DELETE: "ATTENDANCE:DELETE",
  EVENT_VIEW: "EVENT:VIEW",
  EVENT_ADD: "EVENT:ADD",
  EVENT_DELETE: "EVENT:DELETE",
  GROUP_VIEW: "GROUP:VIEW",
  GROUP_ADD: "GROUP:ADD",
  GROUP_UPDATE: "GROUP:UPDATE",
  GROUP_DELETE: "GROUP:DELETE",
  SUBGROUP_VIEW: "SUBGROUP:VIEW",
  SUBGROUP_CREATE: "SUBGROUP:CREATE",
  SUBGROUP_UPDATE: "SUBGROUP:UPDATE",
  SUBGROUP_DELETE: "SUBGROUP:DELETE",
  PARTNER_VIEW: "PARTNER:VIEW",
  PARTNER_CREATE: "PARTNER:CREATE",
  PARTNER_DELETE: "PARTNER:DELETE",
  PARTNER_UPDATE: "PARTNER:UPDATE",
  PARTNER_FIELD_VIEW: "PARTNER_FIELD:VIEW",
  PARTNER_FIELD_CREATE: "PARTNER_FIELD:CREATE",
  PARTNER_FIELD_UPDATE: "PARTNER_FIELD:UPDATE",
  PARTNER_FIELD_DELETE: "PARTNER_FIELD:DELETE",
  USER_VIEW: "USER:VIEW",
  USER_ADD: "USER:ADD",
  USER_UPDATE: "USER:UPDATE",
  USER_DELETE: "USER:DELETE",
  ROLE_MANAGE: "ROLE:MANAGE",
  DATA_VIEW: "DATA:VIEW",
  DATA_ADD: "DATA:ADD",
  DATA_UPDATE: "DATA:UPDATE",
  DATA_DELETE: "DATA:DELETE",
};

const ADMIN_ONLY_ROLES = ["admin", "Super Admin", "Admin"];

// Mirrors the keys of AccessResolver.FIXED_PERMISSIONS in AccessResolver.java.
// These are the 4 hardcoded backend roles (plus role-name aliases) whose
// permissions are defined in code rather than stored in MongoDB.
const FIXED_ROLES = [
  "admin",
  "Super Admin",
  "Admin",
  "special_admin",
  "program_manager",
  "partner",
];

export const admin_only_roles = (roleName = "") =>
  ADMIN_ONLY_ROLES.includes(roleName?.trim?.() || roleName);

export const normalizePrivilegeLabel = (privilegeGroup) => {
  if (!privilegeGroup) {
    return "";
  }

  if (typeof privilegeGroup === "string") {
    return privilegeGroup.trim().toLowerCase();
  }

  return String(privilegeGroup?.label || privilegeGroup?.name || "")
    .trim()
    .toLowerCase();
};

export const PRIVILEGE_LABEL_GROUPS = [
  {
    label: "ADD DATA",
    items: [
      {
        label: "Add Program Data",
        requiredCodes: [
          PERMISSIONS.DATA_ADD,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
      {
        label: "Add Subprogram Data",
        requiredCodes: [
          PERMISSIONS.DATA_ADD,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.SUBPROGRAM_VIEW,
          PERMISSIONS.SUBPROGRAM_ADD,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
      {
        label: "Add Event Data",
        requiredCodes: [
          PERMISSIONS.DATA_ADD,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.EVENT_ADD,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
    ],
  },
  {
    label: "VIEW DATA",
    items: [
      {
        label: "View Subprogram Data",
        requiredCodes: [
          PERMISSIONS.SUBPROGRAM_VIEW,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
      {
        label: "View Event Data",
        requiredCodes: [
          PERMISSIONS.EVENT_VIEW,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
      {
        label: "View Program Data",
        requiredCodes: [PERMISSIONS.PROGRAM_VIEW, PERMISSIONS.DATA_VIEW, PERMISSIONS.PARTNER_VIEW],
      },
    ],
  },
  {
    label: "EVENTS",
    items: [
      {
        label: "View Event",
        requiredCodes: [PERMISSIONS.EVENT_VIEW, PERMISSIONS.PROGRAM_VIEW, PERMISSIONS.PARTNER_VIEW],
      },
    ],
  },
  {
    label: "GROUP PROFILE",
    items: [
      {
        label: "Add Group Profile Data",
        requiredCodes: [
          PERMISSIONS.GROUP_VIEW,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.DATA_ADD,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
      {
        label: "View Group Profile Data",
        requiredCodes: [
          PERMISSIONS.GROUP_VIEW,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
    ],
  },
  {
    label: "SUBGROUP PROFILE",
    items: [
      {
        label: "Add Subgroup Profile Data",
        requiredCodes: [
          PERMISSIONS.GROUP_VIEW,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.SUBGROUP_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.DATA_ADD,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
      {
        label: "View Subgroup Profile Data",
        requiredCodes: [
          PERMISSIONS.GROUP_VIEW,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.SUBGROUP_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.PARTNER_VIEW,
        ],
      },
    ],
  },
  {
    label: "PARTNER FORM",
    items: [
      {
        label: "View Partner Form",
        requiredCodes: [
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.SUBPROGRAM_VIEW,
          PERMISSIONS.PARTNER_VIEW,
          PERMISSIONS.PARTNER_FIELD_VIEW,
          PERMISSIONS.DATA_VIEW,
          PERMISSIONS.DATA_ADD,
        ],
      },
    ],
  },
  // {
  //   label: "ADD PARTNER",
  //   items: [
  //     {
  //       label: "Add Partner",
  //       requiredCodes: [
  //         PERMISSIONS.PARTNER_CREATE,
  //         PERMISSIONS.PROGRAM_VIEW,
  //         PERMISSIONS.SUBPROGRAM_VIEW,
  //       ],
  //     },
  //   ],
  // },
  {
    label: "ATTENDENCE",
    items: [
      {
        label: "Add & Update Attendance",
        requiredCodes: [
          PERMISSIONS.ATTENDANCE_ADD,
          PERMISSIONS.ATTENDANCE_UPDATE,
          PERMISSIONS.ATTENDANCE_VIEW,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.SUBPROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
        ],
      },
      {
        label: "Add Attendance",
        requiredCodes: [
          PERMISSIONS.ATTENDANCE_ADD,
          PERMISSIONS.ATTENDANCE_VIEW,
          PERMISSIONS.PROGRAM_VIEW,
          PERMISSIONS.SUBPROGRAM_VIEW,
          PERMISSIONS.DATA_VIEW,
        ],
      },
    ],
  },
];

const PRIVILEGE_LABEL_CODE_MAP = PRIVILEGE_LABEL_GROUPS.reduce(
  (acc, group) => {
    (group.items || []).forEach((item) => {
      const key = normalizePrivilegeLabel(item.label);
      acc[key] = Array.from(new Set(item.requiredCodes || [])).filter(Boolean);
    });
    return acc;
  },
  {}
);

export const getRequiredCodesForPrivilegeLabel = (privilegeLabel = "") => {
  const key = normalizePrivilegeLabel(privilegeLabel);
  return PRIVILEGE_LABEL_CODE_MAP[key] || [];
};

const getPermissionGroupsForPrivilegeLabels = (requiredPrivilegeLabels = []) => {
  if (!Array.isArray(requiredPrivilegeLabels) || requiredPrivilegeLabels.length === 0) {
    return [];
  }

  const normalizedGroups = Array.isArray(requiredPrivilegeLabels[0])
    ? requiredPrivilegeLabels
    : [requiredPrivilegeLabels];

  return normalizedGroups
    .map((group) => {
      const groupLabels = Array.isArray(group) ? group : [group];
      const codes = Array.from(
        new Set(
          groupLabels.flatMap((label) => getRequiredCodesForPrivilegeLabel(label))
        )
      ).filter(Boolean);
      return codes;
    })
    .filter((codes) => codes.length > 0);
};

export const APP_ROUTE_RULES = [
  { path: "/", publicOnly: true },
  { path: "/login", publicOnly: true },

  {
    path: "/dashboard",
    label: "Dashboard",
    icon: "../images/ico-2.png",
    allowedRoles: FIXED_ROLES,
  },
  {
    path: "/reporting",
    label: "Reporting Section",
    icon: "../images/ico-2.png",
    allowedRoles: FIXED_ROLES,
  },
  {
    path: "/addproject",
    label: "Add Project",
    icon: "../images/add_project.svg",
    allowedRoles: ["Super Admin"],
  },
  {
    path: "/addprogram",
    label: "Add Program",
    icon: "../images/add_program.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/add-fields",
    label: "Add Program Field",
    icon: "../images/add_program_field.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/adduser",
    label: "Add User",
    icon: "../images/add_user.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/addrole",
    label: "Add Role",
    icon: "../images/add_user.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/add-data",
    label: "Add Data",
    icon: "../images/add_program_data.svg",
    requiredPrivilegeLabels: [
      "Add Program Data",
      "Add Subprogram Data",
      "Add Event Data",
    ],
    requiredPermissions: getPermissionGroupsForPrivilegeLabels([
      "Add Program Data",
      "Add Subprogram Data",
      "Add Event Data",
    ]),
  },
  {
    path: "/view-entry-data",
    label: "View Data",
    icon: "../images/add_program_data.svg",
    requiredPrivilegeLabels: [
      "View Program Data",
      "View Subprogram Data",
      "View Event Data",
    ],
    requiredPermissions: getPermissionGroupsForPrivilegeLabels([
      "View Program Data",
      "View Subprogram Data",
      "View Event Data",
    ]),
  },
  {
    path: "/attendance",
    label: "Attendance",
    icon: "../images/add_program_data.svg",
    requiredPrivilegeLabels: [
      "Add Attendance",
      "Add & Update Attendance",
    ],
    requiredPermissions: getPermissionGroupsForPrivilegeLabels([
      "Add Attendance",
      "Add & Update Attendance",
    ]),
  },
  {
    path: "/events",
    label: "Events",
    icon: "../images/add_program_data.svg",
    requiredPrivilegeLabels: ["View Event"],
    requiredPermissions: getPermissionGroupsForPrivilegeLabels(["View Event"]),
  },
  {
    path: "/add-analytics",
    label: "Add Program Analytics",
    icon: "../images/add_program_data.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/dynamic-analytics",
    label: "Dynamic Analytics",
    icon: "../images/add_program_data.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/dynamic-charts",
    label: "Dynamic Charts",
    icon: "../images/add_program_data.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/add-sub-program",
    label: "Add SubProgram",
    icon: "../images/add_program_data.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/add-subprogram-fields",
    label: "Add Sub-Program Field",
    icon: "../images/add_program_field.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/public-form",
    label: "Selection Form",
    icon: "../images/add_program_field.svg",
    requiredPermissions: [PERMISSIONS.DATA_VIEW, PERMISSIONS.DATA_ADD],
  },
  {
    path: "/group-profile",
    label: "Group Profile",
    icon: "../images/add_program_field.svg",
    requiredPrivilegeLabels: ["Add Group Profile Data", "View Group Profile Data"],
    requiredPermissions: getPermissionGroupsForPrivilegeLabels([
      "Add Group Profile Data",
      "View Group Profile Data",
    ]),
  },
  {
    path: "/subgroup-profile",
    label: "Sub Group Profile",
    icon: "../images/add_program_field.svg",
    requiredPrivilegeLabels: ["Add Subgroup Profile Data", "View Subgroup Profile Data"],
    requiredPermissions: getPermissionGroupsForPrivilegeLabels([
      "Add Subgroup Profile Data",
      "View Subgroup Profile Data",
    ]),
  },
  {
    path: "/add-partner",
    label: "Add Partner",
    icon: "../images/add_program_data.svg",
    allowedRoles: ADMIN_ONLY_ROLES,
  },
  {
    path: "/partner-form",
    label: "Partner Form",
    icon: "../images/add_program_data.svg",
    requiredPrivilegeLabels: ["View Partner Form"],
    requiredPermissions: getPermissionGroupsForPrivilegeLabels(["View Partner Form"]),
  },
];

export const APP_ROUTE_MAP = APP_ROUTE_RULES.reduce((acc, rule) => {
  if (acc[rule.path]) {
    warn(`Duplicate route path detected: "${rule.path}" and last definition wins.`);
  }
  acc[rule.path] = rule;
  return acc;
}, {});

export const SIDEBAR_ITEMS = APP_ROUTE_RULES.filter(
  (rule) => rule.sidebar !== false && rule.label && !rule.publicOnly
);

const hasAllowedRole = (roleName = "", allowedRoles = []) => {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  const result = allowedRoles.includes(roleName);
  log(`Role check role="${roleName}" allowed=[${allowedRoles}] result=${result}`);
  return result;
};

const hasRequiredPermissions = (permissions = [], requiredPermissions = []) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  if (permissions.includes(PERMISSIONS.ALL)) {
    log("Permission check bypassed by wildcard permission.");
    return true;
  }

  const normalizedGroups = Array.isArray(requiredPermissions[0])
    ? requiredPermissions
    : [requiredPermissions];

  const result = normalizedGroups.some((group) =>
    group.every((permissionCode) => permissions.includes(permissionCode))
  );

  log(
    `Permission check user=[${permissions}] groups=${JSON.stringify(
      normalizedGroups
    )} result=${result}`
  );

  return result;
};

const hasAnyRequiredPrivilegeLabels = (
  selectedPrivilegeGroups = [],
  requiredPrivilegeLabels = []
) => {
  if (!requiredPrivilegeLabels || requiredPrivilegeLabels.length === 0) {
    return true;
  }

  const normalizedSelectedLabels = new Set(
    (selectedPrivilegeGroups || [])
      .map(normalizePrivilegeLabel)
      .filter(Boolean)
  );

  if (normalizedSelectedLabels.size === 0) {
    return false;
  }

  const normalizedGroups = Array.isArray(requiredPrivilegeLabels[0])
    ? requiredPrivilegeLabels
    : [requiredPrivilegeLabels];

  return normalizedGroups.some((group) => {
    const normalizedLabelGroup = Array.isArray(group) ? group : [group];
    return normalizedLabelGroup.some((label) =>
      normalizedSelectedLabels.has(normalizePrivilegeLabel(label))
    );
  });
};

export const canAccessRouteRule = (rule, access = {}) => {
  if (!rule) {
    warn("canAccessRouteRule called with an undefined rule. Allowing by default.");
    return true;
  }

  if (rule.isFullyPublic || rule.publicOnly) {
    log(`Route "${rule.path}" is public. Access granted.`);
    return true;
  }

  const permissions = access?.permissions || [];
  const selectedPrivilegeGroups = access?.selectedPrivilegeGroups || [];
  const roleName = access?.roleName || "";

  if (!permissions.length && !roleName) {
    warn(`canAccessRouteRule received an empty access object for route "${rule.path}".`);
  }

  const roleOk = hasAllowedRole(roleName, rule.allowedRoles);
  const hasPrivilegeLabelRules =
    Array.isArray(rule.requiredPrivilegeLabels) &&
    rule.requiredPrivilegeLabels.length > 0;
  const hasPrivilegeContext =
    Array.isArray(selectedPrivilegeGroups) &&
    selectedPrivilegeGroups.length > 0;

  const privilegeOk = hasAnyRequiredPrivilegeLabels(
    selectedPrivilegeGroups,
    rule.requiredPrivilegeLabels
  );
  const permOk = hasRequiredPermissions(permissions, rule.requiredPermissions);

  const accessRuleOk = hasPrivilegeLabelRules
    ? hasPrivilegeContext
      ? privilegeOk
      : permOk
    : permOk;

  const result = roleOk && accessRuleOk;

  log(
    `Route "${rule.path}" roleOk=${roleOk} permOk=${permOk} labelOk=${privilegeOk} activeOk=${accessRuleOk} result=${result}`
  );

  return result;
};

const PREFERRED_PATHS = [
  "/dashboard",
  "/add-data",
  "/attendance",
  "/view-entry-data",
  "/events",
  "/group-profile",
  "/subgroup-profile",
  "/public-form",
];

export const getDefaultRoute = (access = {}) => {
  if (!access || (!access.permissions?.length && !access.roleName)) {
    warn("getDefaultRoute received no access context. Redirecting to /login.");
    return "/login";
  }

  const matchingRoute = PREFERRED_PATHS.find((path) =>
    canAccessRouteRule(APP_ROUTE_MAP[path], access)
  );

  if (matchingRoute) {
    return matchingRoute;
  }

  const firstAccessibleSidebarRoute = SIDEBAR_ITEMS.find((item) =>
    canAccessRouteRule(item, access)
  )?.path;

  if (!firstAccessibleSidebarRoute) {
    warn(
      `No preferred or sidebar route matched access=${JSON.stringify(
        access
      )}. Falling back to /login.`
    );
  }

  return firstAccessibleSidebarRoute || "/login";
};
